import { useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { needsOnboarding } from "@/lib/onboarding";
import { makeUpdateProfileSchema } from "@/schemas/me";
import { formatValidationError } from "@/lib/validation-errors";

interface ProfileResp {
  role: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

export const Route = createFileRoute("/onboarding")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : "",
    gate: search.gate === "1" || search.gate === 1 ? "1" : "",
  }),
  beforeLoad: async ({ search }) => {
    const res = await fetch("/api/me/profile");
    if (!res.ok) throw redirect({ to: "/login" });
    const profile = (await res.json()) as ProfileResp;
    if (!needsOnboarding(profile)) {
      throw redirect({ to: search.redirect || "/" });
    }
    return { profile };
  },
  component: OnboardingPage,
});

function splitName(name: string): { first: string; last: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}

function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect: redirectTo, gate } = Route.useSearch();
  const { profile } = Route.useRouteContext();

  const seed = splitName(profile.name || "");
  const [firstName, setFirstName] = useState(profile.firstName || seed.first);
  const [lastName, setLastName] = useState(profile.lastName || seed.last);
  const [phone, setPhone] = useState(profile.phone || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isGate = gate === "1";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const payload = { firstName, lastName, phone };
    // Ista provjera kao na serveru, da greška stigne prije slanja.
    const check = makeUpdateProfileSchema(profile.phone).safeParse(payload);
    if (!check.success) {
      setError(formatValidationError(check.error.issues, payload).error);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/me/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.error || t("onboarding.error"));
        return;
      }
      await navigate({ to: redirectTo || "/" });
    } catch {
      setError(t("onboarding.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-sm">
        <h1
          className="text-2xl font-semibold tracking-tight text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("onboarding.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("onboarding.subtitle")}</p>

        {error && (
          <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="firstName">{t("onboarding.firstName")}</Label>
            <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={100} autoComplete="given-name" className="h-11 rounded-xl bg-white" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lastName">{t("onboarding.lastName")}</Label>
            <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={100} autoComplete="family-name" className="h-11 rounded-xl bg-white" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="phone">{t("onboarding.phone")}</Label>
            <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="tel" className="h-11 rounded-xl bg-white" />
            <p className="text-xs text-muted-foreground">{t("onboarding.phoneHint")}</p>
          </div>

          <Button type="submit" size="lg" disabled={loading || !firstName.trim() || !lastName.trim()} className="h-11 w-full rounded-xl">
            {t("onboarding.save")}
          </Button>

          {!isGate && (
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate({ to: redirectTo || "/" })} className="text-muted-foreground">
              {t("onboarding.later")}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
